import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import BookWallModal from './BookWallModal';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';
import { trackEvent, logError } from '../utils/observability';
import { coLearningInkBody, coLearningInkTitle } from '../ui/coLearningModalTheme';
import CoLearningPromptBackdrop from '../ui/CoLearningPromptBackdrop';
import { useArtistSalonAgeFilter } from '../context/ArtistSalonAgeFilterContext';

const bookSnapshotKey = (uid) => `co-learning:books:${uid || 'anon'}`;

const BookLearningSpace = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { ageFilter, filterSubmissions } = useArtistSalonAgeFilter();
  const [bookActivities, setBookActivities] = useState(() => {
    try {
      const raw = sessionStorage.getItem(bookSnapshotKey(user?.uid));
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [wallModal, setWallModal] = useState(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [userParticipations, setUserParticipations] = useState(new Set());
  const [shareStatusByKey, setShareStatusByKey] = useState({}); // key: `${book}_${activityCode}` → { hasPublic, latestId }
  const [lockedActivity, setLockedActivity] = useState(null); // { book, activityCode }
  const [sharePrompt, setSharePrompt] = useState(null); // { key, latestId, book, activityCode }
  
  // Use ref to track cleanup function and prevent stale closures
  // CRITICAL: Initialize with a no-op function immediately to prevent React from calling undefined
  // This ref MUST ALWAYS contain a function, NEVER a Promise
  const unsubscribeRef = useRef(() => {
    console.log('[BookLearningSpace] Default no-op cleanup called');
  });
  
  // CRITICAL: Validate ref on EVERY render to prevent React from calling a Promise
  // React may call cleanup from previous render BEFORE useEffect runs, so we must validate here
  // This runs synchronously during render, before React can call cleanup
  (() => {
    const current = unsubscribeRef.current;
    if (typeof current !== 'function' || current instanceof Promise) {
      console.warn('[BookLearningSpace] Ref validation failed on render:', typeof current, current instanceof Promise);
      unsubscribeRef.current = () => {
        console.log('[BookLearningSpace] Fallback no-op cleanup (render-time fix)');
      };
    }
  })();

  const artmosphereBooks = [
    "Around the world with 100 artists",
    "What's your story",
    "Complete the story",
    "Impressionism",
    "Pointillism",
  ];

  useEffect(() => {
    try {
      if (!user?.uid) return;
      const raw = sessionStorage.getItem(bookSnapshotKey(user.uid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        setBookActivities(parsed);
      }
    } catch (_) {}
  }, [user?.uid]);

  useEffect(() => {
    try {
      if (!user?.uid) return;
      if (!bookActivities || Object.keys(bookActivities).length === 0) return;
      sessionStorage.setItem(bookSnapshotKey(user.uid), JSON.stringify(bookActivities));
      const previewUrls = Object.values(bookActivities)
        .flatMap((a) => (a?.submissions || []).slice(0, 1))
        .map((s) => s?.bookCoverUrl || s?.bookImage || s?.imageUrl)
        .filter(Boolean)
        .slice(0, 24);
      previewUrls.forEach((src) => {
        try {
          const img = new Image();
          img.decoding = 'async';
          img.src = src;
        } catch (_) {}
      });
    } catch (_) {}
  }, [user?.uid, bookActivities]);

  useEffect(() => {
    // CRITICAL: Initialize unsubscribe function IMMEDIATELY as a no-op
    // This ensures React always has a valid function to call, even from previous renders
    let unsubscribeFn = () => {
      console.log('[BookLearningSpace] No-op cleanup called');
    };
    
    // Update ref immediately
    unsubscribeRef.current = unsubscribeFn;
    
    if (!user) {
      // Return a simple cleanup function that does nothing
      const noUserCleanup = () => {
        console.log('[BookLearningSpace] Cleanup (no user)');
      };
      // CRITICAL: Validate it's a function before returning
      if (typeof noUserCleanup !== 'function' || noUserCleanup instanceof Promise) {
        console.error('[BookLearningSpace] ❌ noUserCleanup is not a function!');
        return () => {}; // Fallback
      }
      return noUserCleanup;
    }
    
    // Set up real-time listener for book activities
    try {
      const result = loadBookActivities();
      console.log('[BookLearningSpace] loadBookActivities returned:', typeof result, result instanceof Promise ? 'Promise' : 'not Promise');
      
      // CRITICAL: Validate and assign synchronously
      if (result && typeof result === 'function' && !(result instanceof Promise)) {
        unsubscribeFn = result;
        unsubscribeRef.current = result;
        console.log('[BookLearningSpace] ✅ Valid unsubscribe function set');
      } else {
        console.error('[BookLearningSpace] ❌ Invalid result type:', typeof result, result instanceof Promise);
        // Keep the no-op function
        unsubscribeFn = () => {
          console.log('[BookLearningSpace] Fallback no-op cleanup');
        };
      }
    } catch (error) {
      console.error('[BookLearningSpace] Error setting up listeners:', error);
      // Keep the no-op function
      unsubscribeFn = () => {
        console.log('[BookLearningSpace] Error fallback cleanup');
      };
    }
    
    // Load participations SEPARATELY - don't let it affect cleanup
    // CRITICAL: Store timeout ID in a way that won't be affected by async operations
    const participationTimeout = setTimeout(() => {
      loadUserParticipations().catch(err => {
        console.error('[BookLearningSpace] Error loading participations:', err);
      });
    }, 0);
    
    // CRITICAL: Capture unsubscribeFn in closure IMMEDIATELY, before any async operations
    // This ensures the cleanup function always has a valid function, never a Promise
    const capturedUnsubscribeFn = unsubscribeFn; // Capture synchronously
    
    // CRITICAL: Create cleanup function that uses CAPTURED value, not the variable
    // This prevents any async operations from affecting what cleanup calls
    const cleanup = () => {
      console.log('[BookLearningSpace] Cleanup function called');
      try {
        clearTimeout(participationTimeout);
        // Use captured value, not the variable - this is guaranteed to be a function
        if (typeof capturedUnsubscribeFn === 'function' && !(capturedUnsubscribeFn instanceof Promise)) {
          capturedUnsubscribeFn();
          console.log('[BookLearningSpace] ✅ Cleanup executed');
        } else {
          console.error('[BookLearningSpace] ❌ Captured unsubscribeFn is invalid:', typeof capturedUnsubscribeFn, capturedUnsubscribeFn instanceof Promise);
        }
      } catch (err) {
        console.error('[BookLearningSpace] Error in cleanup:', err);
      }
    };
    
    // CRITICAL: Validate cleanup is a function before returning
    if (typeof cleanup !== 'function' || cleanup instanceof Promise) {
      console.error('[BookLearningSpace] ❌ CRITICAL: cleanup is not a function!', typeof cleanup, cleanup instanceof Promise);
      return () => {
        console.log('[BookLearningSpace] Emergency fallback cleanup');
      };
    }
    
    console.log('[BookLearningSpace] Returning cleanup function, type:', typeof cleanup, cleanup instanceof Promise ? 'Promise' : 'not Promise');
    console.log('[BookLearningSpace] Captured unsubscribeFn type:', typeof capturedUnsubscribeFn, capturedUnsubscribeFn instanceof Promise ? 'Promise' : 'not Promise');
    return cleanup;
  }, [user]);

  // Lock body scroll and add black background while any overlay is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    const previousBg = document.body.style.backgroundColor;
    if (isOverlayOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.backgroundColor = '#fef3c7';
    } else {
      document.body.style.overflow = previous || '';
      document.body.style.backgroundColor = previousBg || '';
    }
    return () => {
      document.body.style.overflow = previous || '';
      document.body.style.backgroundColor = previousBg || '';
    };
  }, [isOverlayOpen]);

  const loadBookActivities = () => {
    // CRITICAL: This function MUST ALWAYS return a function synchronously, NEVER a Promise
    if (!user) {
      return () => {}; // Return no-op function immediately
    }
    
    let unsubscribe = () => {}; // Default no-op function - MUST be a function
    
    try {
      setLoading(true);
      
      // Use real-time listener for shared/public content (immediate updates)
      const bookArtworksQueryWithOrder = query(
        collection(db, 'bookArtworks'),
        where('visibility', '==', 'public'),
        orderBy('createdAt', 'desc')
      );
      const bookArtworksQueryFallback = query(
        collection(db, 'bookArtworks'),
        where('visibility', '==', 'public')
      );

      const applyBookSnapshot = (snapshot) => {
        const activities = {};

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const book = normalizeBookKeyPart(data.book || data.tag);
          const activityCode = normalizeBookKeyPart(data.activityCode);
          if (!book || !activityCode) return;

          const key = makeKey(book, activityCode);
          const isTaskSeed = !!data.isSystemSeed || data.uid === 'system-book-seed';

          if (!activities[key]) {
            activities[key] = {
              book,
              activityCode,
              submissions: [],
              contributorUids: new Set(),
              totalSubmissions: 0,
              taskPage: null,
            };
          }

          if (isTaskSeed) {
            activities[key].taskPage = {
              id: docSnap.id,
              ...data,
              book,
              activityCode,
            };
            return;
          }

          if (isPublicBookShare(data) && data.uid && data.uid !== 'system-book-seed') {
            activities[key].submissions.push({
              id: docSnap.id,
              ...data,
              book,
              activityCode,
            });
            activities[key].contributorUids.add(data.uid);
          }
        });

        Object.values(activities).forEach((activity) => {
          activity.totalSubmissions = activity.contributorUids.size;
          delete activity.contributorUids;
        });

        console.log('✅ [BOOK] Real-time update received:', Object.keys(activities).length, 'activities');
        setBookActivities(activities);
        setLoading(false);
      };

      const attachBookListener = (q, isFallback = false) => {
        const snapshotResult = onSnapshot(
          q,
          applyBookSnapshot,
          (error) => {
            if (
              !isFallback &&
              error?.code === 'failed-precondition' &&
              String(error?.message || '').includes('index')
            ) {
              console.warn('[BookLearningSpace] Missing index for bookArtworks; using fallback query');
              attachBookListener(bookArtworksQueryFallback, true);
              return;
            }
            console.error('Error in book activities listener:', error);
            setLoading(false);
          },
          { purpose: 'BookLearningSpace-book-activities', component: 'BookLearningSpace' }
        );

        if (snapshotResult && typeof snapshotResult === 'function') {
          unsubscribe = snapshotResult;
        } else {
          unsubscribe = () => {};
        }
      };

      try {
        attachBookListener(bookArtworksQueryWithOrder, false);
      } catch (snapshotError) {
        console.error('[BookLearningSpace] Error setting up snapshot:', snapshotError);
        unsubscribe = () => {};
        setLoading(false);
      }
    } catch (error) {
      console.error('[BookLearningSpace] Error setting up book activities listener:', error);
      unsubscribe = () => {}; // Ensure we have a no-op function
      setLoading(false);
    }
    
    // CRITICAL: Final validation - MUST return a function, never a Promise
    if (typeof unsubscribe !== 'function') {
      console.error('[BookLearningSpace] Final check failed: unsubscribe is not a function:', typeof unsubscribe);
      unsubscribe = () => {}; // Force no-op function
    }
    
    return unsubscribe; // ALWAYS a function at this point
  };

  const normalizeBookKeyPart = (value) => String(value || '').trim();

  const makeKey = (book, activityCode) =>
    `${normalizeBookKeyPart(book)}_${normalizeBookKeyPart(activityCode)}`;

  const parseActivityKey = (key) => {
    const idx = String(key).lastIndexOf('_');
    if (idx <= 0) return { book: '', activityCode: '' };
    return {
      book: key.slice(0, idx),
      activityCode: key.slice(idx + 1),
    };
  };

  const isPublicBookShare = (data) =>
    data?.visibility === 'public' ||
    data?.visibility === true ||
    data?.shareWithArtmosphere === true;

  const userContributedToActivity = (activity) =>
    (activity?.submissions || []).some(
      (submission) =>
        submission?.uid === user?.uid || submission?.userId === user?.uid
    );

  const loadUserParticipations = async (skipCache = false) => {
    if (!user) return;
    
    // Check cache first
    const cacheKey = createCacheKey('bookActivityParticipants', { uid: user.uid }, user.uid);
    const cached = skipCache ? null : getCache(cacheKey);
    if (cached !== null && cached !== undefined) {
      try {
        // Convert cached array back to Set (Sets don't serialize well, so we cache as arrays)
        // Handle both array format (new) and Set format (old, which becomes empty object)
        let participationSet;
        if (Array.isArray(cached)) {
          participationSet = new Set(cached);
        } else if (cached && typeof cached === 'object' && Object.keys(cached).length === 0) {
          // Empty object means it was a Set that got serialized incorrectly - treat as empty
          participationSet = new Set();
        } else {
          // Fallback: try to convert to Set, or create empty Set
          participationSet = new Set();
        }
        console.log('✅ [BOOK] Using cached participations:', participationSet.size);
        setUserParticipations(participationSet);
        if (participationSet.size > 0) {
          await loadUserShareStatus(Array.from(participationSet));
        } else {
          setShareStatusByKey({});
        }
        return;
      } catch (error) {
        console.error('Error parsing cached participations:', error);
        // Clear bad cache and continue to fetch fresh data
        clearCache(cacheKey);
      }
    }
    
    try {
      const qParts = query(
        collection(db, 'bookActivityParticipants'),
        where('uid', '==', user.uid)
      );
      const snap = await getDocs(qParts);
      const keys = new Set();
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.book && data.activityCode) {
          keys.add(makeKey(data.book, data.activityCode));
        }
      });
      
      // Cache as array (Sets don't serialize well with JSON.stringify)
      const participationArray = Array.from(keys);
      setCache(cacheKey, participationArray, 5 * 60 * 1000);
      console.log('✅ [BOOK] Participations cached');
      
      setUserParticipations(keys);
      if (keys.size > 0) {
        await loadUserShareStatus(Array.from(keys));
      } else {
        setShareStatusByKey({});
      }
    } catch (e) {
      console.error('Error loading book participations:', e);
    }
  };

  const loadUserShareStatus = async (keys) => {
    try {
      if (!user) return;
      const status = {};
      for (const key of keys) {
        const { book, activityCode } = parseActivityKey(key);
        if (!book || !activityCode) continue;
        const qUser = query(
          collection(db, 'bookArtworks'),
          where('book', '==', book),
          where('activityCode', '==', activityCode),
          where('uid', '==', user.uid)
        );
        const snap = await getDocs(qUser);
        if (!snap.empty) {
          // Pick latest by createdAt if present
          const docs = snap.docs;
          const latest = docs.reduce((best, cur) => {
            const b = best?.data();
            const c = cur.data();
            const bt = b?.createdAt?.toMillis?.() || 0;
            const ct = c?.createdAt?.toMillis?.() || 0;
            if (ct !== bt) return ct > bt ? cur : best;
            return cur.id > (best?.id || '') ? cur : best;
          }, null);
          const data = latest.data();
          status[key] = { hasPublic: isPublicBookShare(data), latestId: latest.id };
        } else {
          status[key] = { hasPublic: false, latestId: null };
        }
      }
      setShareStatusByKey(status);
    } catch (e) {
      console.error('Error loading book share status:', e);
    }
  };

  const handleActivityClick = (activity) => {
    const key = makeKey(activity.book, activity.activityCode);
    const hasContributed = userContributedToActivity(activity);
    const hasParticipated =
      (userParticipations instanceof Set ? userParticipations.has(key) : false) ||
      hasContributed;
    if (!hasParticipated) {
      setLockedActivity({ book: activity.book, activityCode: activity.activityCode });
      setIsOverlayOpen(true);
      return;
    }
    const share = shareStatusByKey[key];
    if (share?.hasPublic || hasContributed) {
      setSelectedActivity(activity);
      setWallModal(activity.submissions[0]?.id || null);
      setIsOverlayOpen(true);
    } else {
      setSharePrompt({ key, latestId: share?.latestId || null, book: activity.book, activityCode: activity.activityCode });
      setIsOverlayOpen(true);
    }
  };

  const handleCloseModal = () => {
    setWallModal(null);
    setSelectedActivity(null);
    setIsOverlayOpen(false);
  };

  const closeLocked = () => {
    setLockedActivity(null);
    setIsOverlayOpen(false);
  };

  const confirmShareNow = async () => {
    try {
      if (!sharePrompt || !sharePrompt.latestId) {
        setSharePrompt(null);
        setIsOverlayOpen(false);
        return;
      }
      
      // Get the artwork data before updating
      const artworkDoc = await getDoc(doc(db, 'bookArtworks', sharePrompt.latestId));
      const artworkData = artworkDoc.data();
      
      await updateDoc(doc(db, 'bookArtworks', sharePrompt.latestId), {
        visibility: 'public',
        updatedAt: serverTimestamp()
      });
      
      // Update Artist Wall entry to share with Artmosphere (if it exists)
      // Book artworks are always on Artist Wall, we just update shareWithArtmosphere when sharing
      try {
        // Check if artwork already exists in sharedArtworks
        const sharedArtworksQuery = query(
          collection(db, "sharedArtworks"),
          where('artworkId', '==', sharePrompt.latestId),
          where('uid', '==', user.uid)
        );
        const existingShared = await getDocs(sharedArtworksQuery);
        
        if (!existingShared.empty) {
          // Update existing entry to share with Artmosphere
          const existingDoc = existingShared.docs[0];
          await updateDoc(existingDoc.ref, {
            shareWithArtmosphere: true,
            updatedAt: serverTimestamp()
          });
          console.log('✅ Updated Artist Wall entry to share with Artmosphere');
        } else {
          // Create new entry if it doesn't exist (shouldn't happen, but handle it)
          const sharedArtworkDoc = {
            uid: user.uid,
            studentName: user.displayName || user.email || 'Anonymous',
            artworkTitle: `Book Activity: ${artworkData.title || `${sharePrompt.book} - Activity ${sharePrompt.activityCode}`}`,
            artworkUrl: artworkData.imageUrl,
            artworkId: sharePrompt.latestId,
            status: 'active',
            shareWithArtmosphere: true,
            shareWithOrganization: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            type: 'book-activity',
            bookActivityMeta: {
              book: sharePrompt.book,
              activityCode: sharePrompt.activityCode,
              technique: artworkData.technique || '',
              inspiration: artworkData.inspiration || ''
            }
          };
          await addDoc(collection(db, "sharedArtworks"), sharedArtworkDoc);
          console.log('✅ Book artwork saved to Artist Wall');
        }
      } catch (error) {
        console.error('⚠️ Error updating Artist Wall (non-fatal):', error);
        // Don't fail the request if Artist Wall update fails
      }
      
      setShareStatusByKey((prev) => ({
        ...prev,
        [sharePrompt.key]: { hasPublic: true, latestId: sharePrompt.latestId },
      }));
      setUserParticipations((prev) => {
        const next = prev instanceof Set ? new Set(prev) : new Set();
        next.add(sharePrompt.key);
        return next;
      });
      clearCache(createCacheKey('bookActivityParticipants', { uid: user.uid }, user.uid));

      // Refresh status and open wall
      await loadUserShareStatus([sharePrompt.key]);
      const activity = {
        book: sharePrompt.book,
        activityCode: sharePrompt.activityCode,
        submissions: bookActivities[sharePrompt.key]?.submissions || []
      };
      setSharePrompt(null);
      setSelectedActivity(activity);
      setWallModal(activity.submissions[0]?.id || null);
      setIsOverlayOpen(true);
      trackEvent('book_artwork_published', {
        artworkId: sharePrompt.latestId,
        artworkKey: sharePrompt.key,
        userId: user?.uid || null,
      });
    } catch (e) {
      console.error('Error sharing book artwork now:', e);
      setSharePrompt(null);
      setIsOverlayOpen(false);
      logError(e, {
        where: 'BookLearningSpace.confirmShareNow',
        artworkId: sharePrompt?.latestId,
        artworkKey: sharePrompt?.key,
        userId: user?.uid || null,
      });
    }
  };

  const getFilteredActivities = () => {
    if (!selectedBook) {
      return Object.values(bookActivities);
    }
    return Object.values(bookActivities).filter(activity => activity.book === selectedBook);
  };

  // When the public listener includes this user's share, unlock immediately (don't wait on cache).
  useEffect(() => {
    if (!user?.uid || !bookActivities) return;

    const contributedKeys = [];
    Object.entries(bookActivities).forEach(([key, activity]) => {
      if (userContributedToActivity(activity)) contributedKeys.push(key);
    });
    if (contributedKeys.length === 0) return;

    setUserParticipations((prev) => {
      const next = prev instanceof Set ? new Set(prev) : new Set();
      let changed = false;
      contributedKeys.forEach((key) => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setShareStatusByKey((prev) => {
      const next = { ...prev };
      let changed = false;
      contributedKeys.forEach((key) => {
        if (next[key]?.hasPublic) return;
        const mine = (bookActivities[key]?.submissions || []).find(
          (s) => s?.uid === user.uid || s?.userId === user.uid
        );
        if (mine) {
          next[key] = { hasPublic: true, latestId: mine.id };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [bookActivities, user?.uid]);

  useEffect(() => {
    if (!user) return undefined;
    const refreshParticipations = () => {
      loadUserParticipations(true).catch((err) => {
        console.error('[BookLearningSpace] Error refreshing participations:', err);
      });
    };
    window.addEventListener('focus', refreshParticipations);
    return () => window.removeEventListener('focus', refreshParticipations);
  }, [user]);

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-[420px] flex flex-col items-center justify-center py-16 px-4">
        <div
          className="animate-spin rounded-full h-14 w-14 border-t-2 border-amber-500 border-r-2 border-amber-300/60 border-b-2 border-amber-200/40"
          aria-hidden="true"
        />
        <p className="mt-5 text-sm font-semibold text-amber-900 drop-shadow-sm">
          Preparing your Book Co-Learning space…
        </p>
        <p className="mt-1 text-xs text-amber-700/80">
          Loading submissions, unlocks, and reactions.
        </p>
      </div>
    );
  }

  const filteredActivities = getFilteredActivities();

  // With an age filter, only show activities that have public shares from artists in that range.
  const visibleBookActivities = filteredActivities.filter((a) => {
    const publicSubs = filterSubmissions(a.submissions || []);
    if (ageFilter !== 'all') return publicSubs.length > 0;
    return publicSubs.length > 0 || !!a.taskPage;
  });

  return (
    <div className={`space-y-6 ${isOverlayOpen ? 'overflow-hidden' : ''}`}>
      {loading && hasLoadedOnce && (
        <div className="mb-2 text-center text-xs text-amber-700 bg-amber-50/70 border border-amber-200 rounded-full px-3 py-1 w-fit mx-auto">
          Refreshing book learning activities...
        </div>
      )}
      {/* Description text removed - now shown in parent GalleryStudio component above */}

      {/* Book Filter */}
      <div className="flex justify-center mb-6">
        <select
          value={selectedBook}
          onChange={(e) => setSelectedBook(e.target.value)}
          className="px-4 py-2 border border-amber-200 rounded-full bg-white/80 backdrop-blur-sm focus:border-amber-400 focus:outline-none"
        >
          <option value="">All Books</option>
          {artmosphereBooks.map((book) => (
            <option key={book} value={book}>
              {book}
            </option>
          ))}
        </select>
      </div>

      {visibleBookActivities.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No Book Activities Yet
          </h3>
          <p className="text-gray-700 mb-6">
            {ageFilter !== 'all'
              ? 'No book gallery shares from artists in this age range yet. Try another age group or All ages.'
              : selectedBook
                ? `No artworks have been shared for "${selectedBook}" yet.`
                : 'No artworks have been shared from Artmosphere books yet.'}
          </p>
          <button
            onClick={() => navigate('/create-books')}
            className="border-2 border-amber-500 bg-white text-amber-700 px-6 py-3 rounded-full hover:bg-amber-50 hover:border-amber-600 font-semibold transition shadow-md"
          >
            Visit Artmosphere Collections
          </button>
        </div>
      ) : (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${isOverlayOpen ? 'pointer-events-none' : ''}`}>
          {visibleBookActivities.map((activity) => {
            const publicSubs = filterSubmissions(activity.submissions || []);
            const key = makeKey(activity.book, activity.activityCode);
            const hasContributed = userContributedToActivity(activity);
            const hasParticipated =
              (userParticipations instanceof Set ? userParticipations.has(key) : false) ||
              hasContributed;
            const isUnlocked = !!shareStatusByKey[key]?.hasPublic || hasContributed;
            return (
            <div
              key={`${activity.book}_${activity.activityCode}`}
              className={`bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg cursor-pointer ${hasParticipated ? '' : 'opacity-75'}`}
              onClick={() => handleActivityClick(activity)}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="min-w-[76px] px-3 h-12 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 whitespace-nowrap">
                    {activity.activityCode}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/create-books', { state: { selectedBook: activity.book } });
                      }}
                      className="text-left text-xl font-bold text-amber-800 underline decoration-amber-400 hover:text-amber-900 hover:decoration-amber-500"
                      title={`Open ${activity.book} in Artmosphere Collections`}
                    >
                  {activity.book}
                    </button>
                  </div>
                </div>
                {hasParticipated && isUnlocked ? (
                  <div className="text-green-600" title="Unlocked: You have shared your artwork">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  </div>
                ) : (
                  <div className="text-amber-500" title={hasParticipated ? 'Locked: Share to unlock' : 'Complete to unlock'}>
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                  </div>
                )}
              </div>

              {/* Banner Preview - match Daily card style */}
              <div className="relative mb-4">
                <div className={`w-full h-32 rounded-lg border-2 border-amber-200 overflow-hidden bg-amber-50 ${!isUnlocked ? 'filter blur-sm' : ''}`}>
                  <img
                    src={
                      publicSubs[0]?.imageUrl ||
                      activity.taskPage?.imageUrl ||
                      activity.taskPage?.artworkUrl
                    }
                    alt={publicSubs[0]?.title || `${activity.activityCode} activity page`}
                    className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                      <div className="text-center">
                      <div className="text-2xl mb-1">📚</div>
                      <p className="text-xs font-medium text-amber-700">Book Activity</p>
                    </div>
                  </div>
                </div>
                {publicSubs.length > 0 && (
                  <div
                    className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-1 rounded-full"
                    title={`${publicSubs.length} artist${publicSubs.length === 1 ? '' : 's'} shared publicly`}
                  >
                    {publicSubs.length} shared
                  </div>
                )}
                {!isUnlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/30 rounded-full px-3 py-1 text-white text-xs font-semibold flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
                      Locked: Complete and share to unlock
                    </div>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="text-center">
                {!hasParticipated && (
                  <button className="bg-gray-300 text-gray-600 font-semibold py-2 px-6 rounded-full cursor-not-allowed">Complete to Unlock</button>
                )}
                {hasParticipated && !isUnlocked && (
                  <button className="bg-amber-200 text-amber-800 font-semibold py-2 px-6 rounded-full">Share to Unlock</button>
                )}
                {hasParticipated && isUnlocked && (
                  <button className="rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_10px_26px_rgba(234,88,12,0.28)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(234,88,12,0.35)]">Enter Gallery</button>
                )}
              </div>
            </div>
          );})}
        </div>
      )}

      {/* Locked Activity Modal (Book) */}
      {lockedActivity && (
        <CoLearningPromptBackdrop>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className={`mb-4 text-2xl font-bold ${coLearningInkTitle}`}>Activity Locked</h3>
              <p className={`mb-6 ${coLearningInkBody}`}>
                Complete your activity with code <b className="text-[#141210]">{lockedActivity.activityCode}</b> from the Artmosphere books and share your artwork to unlock and see the works by other child artists for this activity.
              </p>
              <div className="flex gap-4 justify-center">
                <button onClick={closeLocked} className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full">Close</button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Share-to-Unlock Prompt (Book) */}
      {sharePrompt && (
        <CoLearningPromptBackdrop>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔓</div>
              <h3 className={`mb-4 text-2xl font-bold ${coLearningInkTitle}`}>Share to Unlock</h3>
              <p className={`mb-6 ${coLearningInkBody}`}>Share your artwork for activity code <b className="text-[#141210]">{sharePrompt.activityCode}</b> to unlock this book wall and see what other child artists created.</p>
              <div className="flex gap-4 justify-center">
                <button onClick={() => { setSharePrompt(null); setIsOverlayOpen(false); }} className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full">Not now</button>
                <button onClick={confirmShareNow} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-full">Share my artwork</button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Book Wall Modal */}
      {wallModal && selectedActivity && (
        <BookWallModal
          book={selectedActivity.book}
          activityCode={selectedActivity.activityCode}
          initialSubmissionId={wallModal}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default BookLearningSpace;
